import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import analytics from '../../../modules/analytics';
import type { RootState } from '../../../store/models';
import { selectors } from '../../../store/CurrentUser.model';
import { SearchPopoverModal } from '../search/SearchPopover';
import { openModal } from '../../shared/login/SignUpLoginModal';

import Header from './Header';
import { useGetUtmParams, useIsMobile } from '../../../hooks';
import useMobileHeaderSearchIcon from './useMobileHeaderSearchIcon';
import { PROFILE_EDIT_ROUTE, PROFILE_ROUTE } from '../../../route-names';

import { useHeaderReferralEntry } from './useHeaderReferralEntry';

export const HeaderContainer: React.FC = () => {
  let isOnProfilePage = false;

  const isMobile = useIsMobile();
  const isLoggedIn = useSelector(selectors.isLoggedIn);
  const providerId = useSelector(selectors.getLoggedInProviderId);
  const stateName = useSelector<RootState, string>(state => state.route.name);
  const utmParams = useGetUtmParams();

  const {
    showDot,
    showMobileSearch,
    dismissDotAndTrackSearchClick,
  } = useMobileHeaderSearchIcon();

  const [searchModalOpen, setSearchModalOpen] = React.useState<boolean>(false);

  const showSetUpBusinessLink = useMemo<boolean>(() => {
    const disallowRoutes = ['provider_vanity', 'provider_signup', 'profile'];
    const isDisallowedRoute = route => stateName.startsWith(route);
    const inDisallowedRoute = disallowRoutes.some(isDisallowedRoute);

    let result: boolean = false;
    if (typeof stateName !== 'string') {
      return result;
    }

    if (!inDisallowedRoute) {
      result = isMobile && !providerId && !isLoggedIn && !showMobileSearch;
    }

    return result;
  }, [isMobile, providerId, stateName, isLoggedIn, showMobileSearch]);

  if (typeof stateName === 'string') {
    if (
      stateName.startsWith(PROFILE_ROUTE)
      || stateName.startsWith(PROFILE_EDIT_ROUTE)
    ) {
      isOnProfilePage = true;
    }
  }

  const handleClickSetUpMyBusiness = e => {
    e.preventDefault();
    analytics.track('provider_homepage_set_up_my_business_link_click', { origin: 'header_nav' });
    openModal('signup', 'header_nav');
  };

  const handleMobileSearchClick = React.useCallback(() => {
    dismissDotAndTrackSearchClick();
    setSearchModalOpen(true);
  }, [dismissDotAndTrackSearchClick]);

  const handleMobileSearchDismiss = React.useCallback(() => {
    setSearchModalOpen(false);
  }, []);

  const handleDirectNavigation = useCallback((route: string) => {
    const profileRoutes = [PROFILE_ROUTE, PROFILE_EDIT_ROUTE];
    if (
      route === stateName
      || (profileRoutes.includes(route) && profileRoutes.includes(stateName))
    ) {
      // Since it's not on its own route, navigation will not touch the search modal if it's
      // summoned from the header and the header is not re-rendered. So if we're navigating on the
      // same screen (most likely the profile), just close the modal.
      setSearchModalOpen(false);
    }
  }, [setSearchModalOpen, stateName]);

  const referralShareProps = useHeaderReferralEntry(isOnProfilePage);

  // entirely hide the header on mobile search when logged in
  if (isMobile && typeof stateName === 'string' && stateName.startsWith('search') && isLoggedIn) {
    return null;
  }

  const showReferralEntry = (
    !!referralShareProps
    && (isOnProfilePage || isLoggedIn || !isMobile)
  );

  return (
    <React.Fragment>
      <Header
        showSidebarToggle={isMobile}
        providerId={providerId}
        showSearch={isOnProfilePage}
        showMobileBorder={isOnProfilePage}
        onMobileSearchClick={handleMobileSearchClick}
        onClickSetUpMyBusiness={handleClickSetUpMyBusiness}
        showMobileSearchDot={showDot}
        showMobileSearch={showMobileSearch}
        showSetUpBusinessLink={showSetUpBusinessLink}
        showReferralEntry={showReferralEntry}
        referralShareProps={referralShareProps}
        utmParams={utmParams}
      />
      {searchModalOpen && (
        <SearchPopoverModal
          source="client_mobile_header_search_btn"
          onClose={handleMobileSearchDismiss}
          onDirectNavigation={handleDirectNavigation}
        />
      )}
    </React.Fragment>
  );
};

export default HeaderContainer;
